<template>
  <div class="confirm-order-wrapper">
    <transition name="mask">
      <div class="drawer-mask" @click="handleClose" v-show="isShow"></div>
    </transition>
    <transition name="drawer">
      <div class="confirm-order-drawer" v-if="isShow">
        <div class="drawer-container">
          <div class="drawer-header">
            <h3 class="drawer-title">确认订单</h3>
            <button class="drawer-close" @click="handleClose">
              <KyIcon type="close" size="18" color="#666" />
            </button>
          </div>

          <div class="drawer-body">
            <div class="address-info" @click="showAddressEdit = true">
              <KyIcon type="location" size="20" color="#333" />
              <div
                class="address-info-container"
                v-if="
                  infoParams &&
                  infoParams.user_address &&
                  infoParams.user_name &&
                  infoParams.user_phone
                "
              >
                <p class="address-detail">{{ infoParams.user_address }}</p>
                <p class="contact-info">
                  {{ infoParams.user_name }}
                  {{ formatPhone(infoParams.user_phone) }}
                </p>
              </div>
              <div class="address-info-container" v-else>
                <p class="empty-address address-detail">请完善收货信息</p>
              </div>
              <KyIcon
                type="back"
                size="16"
                color="#999"
                class="edit-address-btn"
              />
            </div>

            <div class="goods-info">
              <img
                :src="goods.image_thumb"
                :alt="goods.name"
                class="goods-img"
              />
              <div class="goods-desc">
                <p class="goods-name">{{ goods.name }}</p>
                <p class="goods-price">
                  ¥{{ goods.price }}
                  <del style="color: #999" v-if="goods.originalPrice"
                    >¥{{ goods.originalPrice }}</del
                  >
                </p>
              </div>
            </div>

            <div class="goods-attr">
              <div class="attr-item">
                <span class="attr-label">数量</span>
                <div class="quantity-control">
                  <div class="quantity-btn" @click="decreaseQuantity">-</div>
                  <el-input
                    v-model.number="quantity"
                    class="quantity-input"
                    size="small"
                    :min="1"
                    @input="handleInput"
                    @blur="handleBlur"
                  />
                  <div class="quantity-btn" @click="increaseQuantity">+</div>
                </div>
              </div>
              <div class="attr-item">
                <span class="attr-label">配送</span>
                <span class="attr-value">邮局配送</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">备注</span>
                <el-input
                  v-model="remark"
                  placeholder="选填，建议先与商家协商一致"
                  maxlength="100"
                  class="custom-input2"
                ></el-input>
              </div>
            </div>
          </div>

          <div class="drawer-footer">
            <div>
              <div class="total-price">
                ¥{{ calculateTotalPrice().toFixed(2) }}
              </div>
              <div class="freight">
                {{ shipping.fee === 0 ? "免运费" : "运费: ¥" + shipping.fee }}
              </div>
            </div>
            <button class="submit-btn" @click="handleSubmit">提交订单</button>
          </div>
        </div>
      </div>
    </transition>
    <AddressEdit
      :isInfoShow="showAddressEdit"
      :info="infoParams"
      @save="handleAddressSave"
      @close="showAddressEdit = false"
    />
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  watch,
  defineProps,
  defineEmits,
  toRefs,
  onMounted,
} from "vue";
import AddressEdit from "@/components/AddressEdit/";
import KyIcon from "@/components/KyIcon";

const props = defineProps({
  // 控制弹窗显示与隐藏
  isShow: {
    type: Boolean,
    default: false,
  },
  // 商品信息
  goods: {
    type: Object,
    default: () => ({}),
  },
  // 配送信息
  shipping: {
    type: Object,
    default: () => ({
      fee: 0, // 0表示免运费
    }),
  },
  // 优惠金额
  discount: {
    type: Number,
    default: 0,
  },
  // 初始数量
  initialQuantity: {
    type: Number,
    default: 1,
  },
});

onMounted(() => {
  // 读取本地存储的数据
  const storedInfo = localStorage.getItem("infoParams");

  // 判断是否有本地数据
  if (storedInfo) {
    try {
      // 解析JSON字符串为对象
      const parsedInfo = JSON.parse(storedInfo);
      // 赋值给响应式对象
      Object.assign(infoParams, parsedInfo);
    } catch (error) {
      console.error("解析本地地址数据失败：", error);
      // 出错时清空无效数据
      localStorage.removeItem("infoParams");
    }
  }
});

// 解构props，转换为响应式引用
const { isShow, goods, shipping, discount, initialQuantity } = toRefs(props);
// 定义组件发出的事件
const emit = defineEmits(["close", "quantity-change", "submit"]);

// 组件内部状态
const showAddressEdit = ref(false);
const quantity = ref(initialQuantity.value);
const infoParams = reactive({
  user_name: "",
  user_address: "",
  user_phone: "",
  city: "",
});
const remark = ref("");

// 关闭弹窗，通知父组件
const handleClose = () => {
  emit("close");
};

// 减少数量
const decreaseQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
    emit("quantity-change", quantity.value);
  }
};

// 增加数量
const increaseQuantity = () => {
  quantity.value++;
  emit("quantity-change", quantity.value);
};

const handleInput = (val) => {
  // 过滤非数字，确保不小于1
  const num = parseInt(val.replace(/\D/g, "")) || 1;
  quantity.value = Math.max(num, 1);
  // 输入时实时触发事件
  emit("quantity-change", quantity.value);
};

// 失焦时修正（确保最终值合法）
const handleBlur = () => {
  if (quantity.value < 1) {
    quantity.value = 1;
    emit("quantity-change", quantity.value);
  }
};

const formatPhone = (phone) => {
  if (!phone) return "";
  if (/^\d{11}$/.test(phone)) {
    return phone.replace(/^(\d{3})(\d{4})(\d{4})$/, "$1****$3");
  }
  return phone;
};

// 提交订单，通知父组件
const handleSubmit = () => {
  const orderInfo = {
    count: quantity.value, //数量
    user_name: infoParams.user_name,
    user_address: infoParams.user_address,
    user_phone: infoParams.user_phone,
    city: infoParams.city, //地区
    total: calculateTotalPrice(), //总价值
    remark: remark.value,
  };
  emit("submit", orderInfo);
};

// 处理地址保存
const handleAddressSave = (infoData) => {
  Object.assign(infoParams, infoData);

  localStorage.setItem("infoParams", JSON.stringify(infoData));
};

// 计算总价
const calculateTotalPrice = () => {
  return (
    goods.value.price * quantity.value + shipping.value.fee - discount.value
  );
};

// 监听初始数量变化
watch(
  () => props.initialQuantity, // 监听 props 中的初始值（避免直接监听 ref）
  (newVal) => {
    // 确保新值合法（不小于1）
    if (newVal >= 1) {
      quantity.value = newVal;
      // 初始值变化时也触发事件（可选，根据需求决定）
      emit("quantity-change", quantity.value);
    }
  },
  { immediate: true } // 初始化时立即执行一次
);

// 监听地址编辑弹窗状态变化
// watch(showAddressEdit, (newVal) => {
//   console.log("父组件弹窗状态变化：", newVal);
// });
</script>

<style lang="less" scoped>
@color-primary: #409eff;
@color-danger: #f56c6c;
@color-text-primary: #303133;
@color-text-secondary: #606266;
@color-text-placeholder: #909399;
@color-border: #e4e7ed;
@color-bg-light: #fff;
@border-radius: 4px;
@transition-duration: 0.3s;
@transition-base: all 0.3s linear;

:deep(.el-input__wrapper) {
  padding: 1px 0px;
}

.custom-input2 {
  width: 80%;
}

.quantity-input {
  width: 30px;
}

:deep(.quantity-input .el-input__inner) {
  text-align: center;
}

:deep(.custom-input2 .el-input__wrapper) {
  box-shadow: none !important;
}

:deep(.custom-input2 .el-input__inner) {
  padding: 0 !important;
  font-size: 16px;
  box-shadow: none !important;
  text-align: right;
}

.drawer-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateY(100%);
}
.drawer-enter-to,
.drawer-leave-from {
  transform: translateY(0);
}

.mask-enter-active,
.mask-leave-active {
  transition: opacity 0.3s ease;
}

.mask-enter-from,
.mask-leave-to {
  opacity: 0;
}

.mask-enter-to,
.mask-leave-from {
  opacity: 1;
}

.confirm-order-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100000;
  overflow: hidden;

  .drawer-container {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 90vh;
    background-color: #fff;
    border-top-left-radius: @border-radius;
    border-top-right-radius: @border-radius;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
  }

  .drawer-header {
    padding: 10px 10px 0 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .drawer-title {
      margin: 0;
      font-size: 16px;
      color: @color-text-primary;
      font-weight: 500;
    }

    .drawer-close {
      background: none;
      border: none;
      padding: 5px;
      cursor: pointer;
      color: @color-text-secondary;
      transition: color @transition-duration;
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 0 10px;

    .address-info {
      display: flex;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid @color-border;
      gap: 10px;

      .address-info-container {
        flex: 1;
        text-align: left;

        .address-detail {
          color: @color-text-primary;
          word-break: break-word;
        }

        .contact-info {
          margin: 0;
          color: @color-text-placeholder;
          font-size: 14px;
        }

        .empty-address {
          color: @color-text-placeholder;
        }
      }

      .edit-address-btn {
        transform: rotate(180deg);
      }
    }

    .goods-info {
      display: flex;
      padding: 10px 0;

      .goods-img {
        width: 100px;
        height: 100px;
        object-fit: contain;
        margin-right: 10px;
        border-radius: @border-radius;
        background-color: #f7f7f7;
      }

      .goods-desc {
        flex: 1;
        text-align: left;

        .goods-name {
          margin: 0 0 5px 0;
          color: @color-text-primary;
          font-size: 14px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .goods-price {
          color: @color-danger;
          margin: 8px 0 0 0;
          font-weight: 400;
        }
      }
    }

    .goods-attr {
      .attr-item {
        display: flex;
        justify-content: space-between;
        padding: 12px 0;

        &:last-child {
          border-bottom: none;
        }

        .attr-label {
          display: flex;
          align-items: center;
          color: @color-text-placeholder;
        }

        .attr-value {
          text-align: right;
          color: @color-text-primary;
        }

        .quantity-control {
          display: flex;
          align-items: center;

          .quantity-btn {
            width: 24px;
            height: 24px;
            line-height: 22px;
            text-align: center;
            font-size: 18px;
            background-color: @color-bg-light;
            cursor: pointer;
            border-radius: 50%;
            transition: @transition-base;
            user-select: none;
          }
        }
      }
    }
  }

  .drawer-footer {
    padding: 5px 10px calc(10px + env(safe-area-inset-bottom, 0px)) 10px;
    border-top: 1px solid @color-border;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .total-price {
      font-size: 18px;
      color: @color-danger;
      font-weight: 600;
    }

    .freight {
      font-size: 12px;
      color: @color-text-placeholder;
      margin-top: 3px;
      text-align: right;
    }

    .submit-btn {
      background-color: @color-danger;
      color: #fff;
      border: none;
      padding: 8px 20px;
      border-radius: @border-radius;
      cursor: pointer;
      font-size: 14px;
      transition: @transition-base;
    }
  }
}
</style>
